import { createSlice } from '@reduxjs/toolkit';
import firebaseService from '@ameroservices-platform/shared/services/frontendFirebase';
import orderState from '../../ui-components/states/orderState';
import { setCartOrderUid } from '@ameroservices-platform/attraction-frontend/app/auth/store/userSlice';

const userSlice = createSlice({
	name: 'userApp/user',
	initialState: {
		user: null,
		loading: false,
		cart: null,
		cartOrderLines: [],
		cartDraft: null,
		cartError: null,
		cartLoading: {},
		orders: [],
		tickets: [],
		validations: {},
		subCustomerTickets: {},
		subscriptions: [],
		orderLines: {},
		subscriptionOrders: {},
		associatedCustomers: null
	},
	reducers: {
		setUser(state, action) {
			state.user = action.payload;
		},
		setLoading(state, action) {
			state.loading = action.payload;
		},
		setCart(state, action) {
			state.cart = action.payload;
		},
		setCartDraft(state, action) {
			state.cartDraft = action.payload;
		},
		setCartOrderLines(state, action) {
			state.cartOrderLines = action.payload;
		},
		setCartError(state, action) {
			state.cartError = action.payload;
		},
		updateCart(state, action) {
			state.cart = { ...state.cart, ...action.payload };
		},
		setCartLoading(state, action) {
			state.cartLoading[action.payload.key] = action.payload.value;
		},
		setTickets(state, action) {
			state.tickets = action.payload;
		},
		setValidations(state, action) {
			state.validations[action.payload.ticketUid] = action.payload.validations;
		},
		setOrders(state, action) {
			state.orders = action.payload;
		},
		setSubscriptions(state, action) {
			state.subscriptions = action.payload;
		},
		setSubscriptionTypes(state, action) {
			state.subscriptionTypes = action.payload;
		},
		setAssociatedCustomers(state, action) {
			state.associatedCustomers = action.payload;
		},
		setSubscriptionOrders(state, action) {
			state.subscriptionOrders = { ...state.subscriptionOrders, ...action.payload };
		},
		setSubscriptionOrdersV2(state, action) {
			state.subscriptionOrders[action.payload.subscriptionUid] = action.payload.orders;
		},
		setOrderLines(state, action) {
			const orderLines = { ...state.orderLines };

			orderLines[action.payload.orderUid] = [];

			action.payload.orderLines.forEach(orderLine => {
				orderLines[action.payload.orderUid].push(orderLine);
			});
			state.orderLines = orderLines;
		},
		setSubCustomerTickets(state, action) {
			state.subCustomerTickets[action.payload.customerUid] = action.payload.tickets;
		}
	}
});

export const {
	setUser,
	setLoading,
	setCart,
	setCartDraft,
	setCartOrderLines,
	setCartError,
	setOrders,
	setOrderLines,
	setTickets,
	setValidations,
	setSubscriptions,
	setSubscriptionTypes,
	setAssociatedCustomers,
	setSubscriptionOrders,
	setSubCustomerTickets,
	setSubscriptionOrdersV2,
	updateCart,
	setCartLoading
} = userSlice.actions;

export default userSlice.reducer;

export const cartListener = (customerUid, orderUid) => dispatch => {
	dispatch(setCartLoading({ key: 'order', value: true }));
	const db = firebaseService.getOrganisationRootDB();
	return db
		.collection('customers')
		.doc(customerUid)
		.collection('orders')
		.doc(orderUid)
		.onSnapshot(doc => {
			dispatch(setCart({ ...doc.data(), id: doc.id }));
			dispatch(setCartLoading({ key: 'order', value: false }));
		});
};

export const cartOrderLinesListener = (customerUid, orderUid) => dispatch => {
	const db = firebaseService.getOrganisationRootDB();
	return db
		.collection('customers')
		.doc(customerUid)
		.collection('orders')
		.doc(orderUid)
		.collection('orderLines')
		.where('deleted', '==', false)
		.onSnapshot(query => {
			const orderLines = query.docs.map(doc => ({ ...doc.data(), id: doc.id }));
			orderLines.sort(
				(a, b) =>
					(a && a.createdAt && a.createdAt.toDate ? a.createdAt.toDate() : 0) -
					(b && b.createdAt && b.createdAt.toDate ? b.createdAt.toDate() : 0)
			);
			dispatch(setCartOrderLines(orderLines));
		});
};

export const orderLinesListener = (customerUid, orderUid) => dispatch => {
	const db = firebaseService.getOrganisationRootDB();
	return (
		db
			.collection('customers')
			.doc(customerUid)
			.collection('orders')
			.doc(orderUid)
			.collection('orderLines')
			// .where('state', '==', 'complete') TODO
			.onSnapshot(
				docs => {
					const orderLines = docs.docs.reduce((acc, cur) => {
						acc.push({ ...cur.data(), id: cur.id });
						return acc;
					}, []);

					dispatch(setOrderLines({ orderLines, orderUid }));
				},
				error => console.error('orderLinesListener', { customerUid, error })
			)
	);
};

export const ordersListener = customerUid => dispatch => {
	const db = firebaseService.getOrganisationRootDB();
	return db
		.collection('customers')
		.doc(customerUid)
		.collection('orders')
		.where('deleted', '==', false)
		.where('state', 'in', ['complete', 'pendingPayment', 'error', 'refunded'])
		.onSnapshot(
			docs => {
				const orders = docs.docs.reduce((acc, cur) => {
					const data = cur.data();
					data.payments = data.payments.map(payment => ({
						...payment,
						updatedAt: payment.updatedAt ? payment.updatedAt.toDate().getTime() : undefined
					}));
					acc.push({
						...data,
						createdAt: data.createdAt ? data.createdAt.toDate().getTime() : undefined,
						updatedAt: data.updatedAt ? data.updatedAt.toDate().getTime() : undefined,
						id: cur.id
					});
					return acc;
				}, []);

				orders.sort((a, b) => b.createdAt - a.createdAt);
				dispatch(setOrders(orders));
			},
			error => console.error('ordersListener', { customerUid, error })
		);
};

export const ticketsForSubCustomerListener = customerUid => dispatch => {
	const db = firebaseService.getOrganisationRootDB();
	return db
		.collection('customers')
		.doc(customerUid)
		.collection('customer_tickets')
		.where('deleted', '==', false)
		.onSnapshot(
			docs => {
				const tickets = docs.docs.reduce((acc, cur) => {
					const ticket = cur.data();
					delete ticket.validations;
					if (ticket.event) {
						ticket.event.start = ticket.event.start ? ticket.event.start.toDate().getTime() : undefined;
						ticket.event.end = ticket.event.end ? ticket.event.end.toDate().getTime() : undefined;
					}
					acc.push({
						...ticket,
						updatedAt: ticket.updatedAt ? ticket.updatedAt.toDate().getTime() : undefined,
						id: cur.id
					});
					return acc;
				}, []);
				tickets.sort((a, b) => b.issueDate - a.issueDate);
				dispatch(setSubCustomerTickets({ customerUid, tickets }));
			},
			error => console.error('ticketsListener', { customerUid, error })
		);
};

export const ticketsListener = customerUid => dispatch => {
	const db = firebaseService.getOrganisationRootDB();
	return db
		.collection('customers')
		.doc(customerUid)
		.collection('customer_tickets')
		.where('deleted', '==', false)
		.onSnapshot(
			docs => {
				const tickets = docs.docs.reduce((acc, cur) => {
					const ticket = cur.data();
					delete ticket.validations;
					if (ticket.event) {
						ticket.event.start = ticket.event.start ? ticket.event.start.toDate().getTime() : undefined;
						ticket.event.end = ticket.event.end ? ticket.event.end.toDate().getTime() : undefined;
					}
					acc.push({
						...ticket,
						updatedAt: ticket.updatedAt ? ticket.updatedAt.toDate().getTime() : undefined,
						id: cur.id
					});
					return acc;
				}, []);
				tickets.sort((a, b) => b.issueDate - a.issueDate);
				dispatch(setTickets(tickets));
			},
			error => console.error('ticketsListener', { customerUid, error })
		);
};

export const validationsListener = (customerUid, ticketUid) => dispatch => {
	const db = firebaseService.getOrganisationRootDB();
	return db
		.collection('customers')
		.doc(customerUid)
		.collection('customer_tickets')
		.doc(ticketUid)
		.collection('validations')
		.where('deleted', '==', false)
		.where('success', '==', true)
		.where('type', '==', 'use')
		.onSnapshot(
			docs => {
				const validations = docs.docs.reduce((acc, cur) => {
					const validation = cur.data();
					validation.date = validation.date ? validation.date.toDate().getTime() : undefined;
					acc.push({
						...validation,
						id: cur.uid
					});
					return acc;
				}, []);
				dispatch(setValidations({ ticketUid, validations }));
			},
			error => console.error('validationsListener', { customerUid, error })
		);
};

export const subscriptionsListener = customerUid => dispatch => {
	const db = firebaseService.getOrganisationRootDB();
	return db
		.collection('customers')
		.doc(customerUid)
		.collection('customer_subscriptions')
		.where('deleted', '==', false)
		.onSnapshot(
			docs => {
				let subscriptions = docs.docs.reduce((acc, cur) => {
					acc.push({ ...cur.data(), id: cur.id });
					return acc;
				}, []);

				subscriptions = subscriptions.map(subscription => ({
					...subscription,
					nextPeriodStart: subscription.nextPeriodStart
						? subscription.nextPeriodStart.toDate().getTime()
						: undefined,
					start: subscription.start ? subscription.start.toDate().getTime() : undefined,
					end: subscription.end ? subscription.end.toDate().getTime() : undefined
				}));
				subscriptions.sort((a, b) => b.start - a.start);

				dispatch(setSubscriptions(subscriptions));
			},
			error => console.error('subscriptionsListener', { customerUid, error })
		);
};

export const subscriptionTypesListener = () => dispatch => {
	const db = firebaseService.getOrganisationRootDB();
	return db
		.collection('subscriptionTypes')
		.where('deleted', '==', false)
		.onSnapshot(query => {
			dispatch(setSubscriptionTypes(query.docs.map(doc => ({ ...doc.data(), id: doc.id }))));
		});
};

export const ordersListeners = (customerUid, subscriptionUid) => dispatch => {
	const db = firebaseService.getOrganisationRootDB();
	return db
		.collection('customers')
		.doc(customerUid)
		.collection('orders')
		.where('subscriptionUid', '==', subscriptionUid)
		.onSnapshot(query => {
			dispatch(
				setSubscriptionOrdersV2({
					subscriptionUid: subscriptionUid,
					orders: query.docs.map(doc => ({ ...doc.data(), id: doc.id }))
				})
			);
		});
};

export const orderListeners = (customerUid, subscriptionUids) => dispatch => {
	const db = firebaseService.getOrganisationRootDB();
	const unsubs = subscriptionUids.map(subscriptionUid =>
		db
			.collection('customers')
			.doc(customerUid)
			.collection('orders')
			.where('subscriptionUid', '==', subscriptionUid)
			.where('state', '==', orderState.PENDING_PAYMENT)
			.orderBy('createdAt', 'desc')
			.limit(1)
			.onSnapshot(
				docs => {
					const orders = docs.docs.reduce((acc, cur) => {
						const data = cur.data();
						acc[data.subscriptionUid] = { ...cur.data(), id: cur.id };
						return acc;
					}, {});

					dispatch(setSubscriptionOrders(orders));
				},
				error => console.error('orderListeners', { customerUid, error })
			)
	);

	return unsubs;
};

export const getEventsByUids = async eventUids => {
	if (!eventUids) {
		return {};
	}
	const db = firebaseService.getRootDB();
	const eventDocs = await Promise.all(
		eventUids
			.filter(eventUid => eventUid)
			.map(eventUid => {
				return db
					.collectionGroup('events')
					.where('organisationUid', '==', firebaseService.getOrganisationId())
					.where('deleted', '==', false)
					.where('uid', '==', eventUid)
					.get();
			})
	);
	const es = {};
	eventDocs.forEach(eventDoc => {
		eventDoc.forEach(o => {
			const data = o.data();
			es[o.id] = { ...data, id: o.id, start: data.start.toMillis(), end: data.end.toMillis() };
		});
	});

	return es;
};

export const associatedCustomersListener = customerId => dispatch => {
	const db = firebaseService.getOrganisationRootDB();
	return db
		.collection('customers')
		.where('deleted', '==', false)
		.where('isAssociated', '==', true)
		.where('associatedCustomer', '==', customerId)
		.onSnapshot(
			querySnapshot => {
				const customers = [];
				querySnapshot.forEach(doc => {
					const docData = doc.data();
					customers.push({
						...docData,
						id: doc.id
					});
				});
				dispatch(setAssociatedCustomers(customers));
			},
			error => console.error('associatedCustomersListener', { customerId, error })
		);
};

export const userListener = customerUid => dispatch => {
	const db = firebaseService.getOrganisationRootDB();
	return db
		.collection('customers')
		.doc(customerUid)
		.onSnapshot(
			q => {
				dispatch(setUser({ ...q.data(), id: q.id, uid: q.id }));
			},
			error => console.error('userListener', { customerUid, error })
		);
};

export const requestCartDraft = (customerUid, orderUid) => dispatch => {
	const db = firebaseService.getOrganisationRootDB();
	return db
		.collection('customers')
		.doc(customerUid)
		.collection('orderDrafts')
		.doc(orderUid)
		.get()
		.then(doc => {
			dispatch(setCartDraft({ ...doc.data(), id: doc.id }));
		});
};

export const pageListener = (uri, callback) => {
	const db = firebaseService.getContentRootDB();
	return db
		.collection('pages')
		.where('uri', '==', uri)
		.where('deleted', '==', false)
		.onSnapshot(query => {
			const pages = query.docs.map(doc => {
				const data = doc.data();
				if (data.content && typeof data.content === 'string') {
					data.content = JSON.parse(data.content);
				}
				return { ...data, id: doc.id };
			});
			callback(pages?.[0] ?? null);
		});
};

export const updateUser = (customerUid, data) => async dispatch => {
	dispatch(setLoading(true));
	const db = firebaseService.getOrganisationRootDB();
	await db.collection('customers').doc(customerUid).update(data);
	dispatch(setLoading(false));
};

export const updateSubscriptionPaymentType = async (subscriptionUid, paymentType) => {
	const response = await firebaseService.callFunctionByName('organisationCustomerSubscriptionsSetPaymentType', {
		subscriptionUid,
		paymentType
	});
	return response.data;
};

export const createSubscriptionMandate = async subscriptionUid => {
	const response = await firebaseService.callFunctionByName(
		'organisationCustomerSubscriptionsCreateSubscriptionMandate',
		{
			subscriptionUid
		}
	);
	return response.data;
};

export const cancelSubscriptionPayment = async subscriptionUid => {
	const response = await firebaseService.callFunctionByName(
		'organisationCustomerSubscriptionsCancelSubscriptionPayment',
		{
			subscriptionUid
		}
	);
	return response.data;
};

export const updateCustomerFunction = (customerUid, data) => async dispatch => {
	dispatch(setLoading(true));
	await firebaseService.callFunctionByName('organisationCustomerUpdateCustomer', {
		customerUid,
		...data,
		frontend: true
	});
	dispatch(setLoading(false));
};

export const sendSubscriptionConfirmationEmail = url => async dispatch => {
	dispatch(setLoading(true));
	console.log(url);
	await firebaseService.callFunctionByName('organisationCustomerSendSubscriptionConfirmationMail', { url });
	dispatch(setLoading(false));
};

export const sendConfirmationEmail = autoSend => async dispatch => {
	dispatch(setLoading(true));
	const currentLanguageLocalStorage = localStorage.getItem('language')
		? JSON.parse(localStorage.getItem('language'))
		: null;
	await firebaseService.callFunctionByName('organisationCustomerSendConfirmationMail', {
		autoSend,
		language:
			currentLanguageLocalStorage && currentLanguageLocalStorage.isoCode
				? currentLanguageLocalStorage.isoCode
				: null
	});
	dispatch(setLoading(false));
};

export const confirmEmail = oobCode => async dispatch => {
	dispatch(setLoading(true));
	await firebaseService.callFunctionByName('organisationCustomerConfirmEmail', { oobCode });
	dispatch(setLoading(false));
};
export const activateUser = (oobCode, password) => async dispatch => {
	dispatch(setLoading(true));
	await firebaseService.callFunctionByName('organisationCustomerActivateUser', { oobCode, password });
	dispatch(setLoading(false));
};
export const updateCustomerByToken = (token, contactInfo) => async dispatch => {
	dispatch(setLoading(true));
	const resp = await firebaseService
		.callFunctionByName('organisationCustomerUpdateCustomerByToken', {
			token,
			contactInfo
		})
		.then(response => response.data)
		.catch(() => false);
	dispatch(setLoading(false));
	return resp;
};
export const getUserByToken = async token => {
	const resp = await firebaseService.callFunctionByName('organisationCustomerGetUserByToken', {
		token
	});
	return resp.data;
};

export const verifyCustomer = async email => {
	const resp = await firebaseService.callFunctionByName('organisationCustomerVerifyCustomer', { email });
	return resp.data;
};

export const cartCheck = () => async dispatch => {
	dispatch(setCartLoading({ key: 'cartCheck', value: true }));
	const currentLanguageLocalStorage = localStorage.getItem('language')
		? JSON.parse(localStorage.getItem('language'))
		: null;
	const response = await firebaseService.callFunctionByName('organisationCustomerCartCheckCart', {
		language:
			currentLanguageLocalStorage && currentLanguageLocalStorage.isoCode
				? currentLanguageLocalStorage.isoCode
				: null
	});
	if (response?.data?.orderUid) {
		dispatch(setCartOrderUid(response.data.orderUid));
	}
	dispatch(setCartLoading({ key: 'cartCheck', value: false }));
	dispatch(setCartLoading({ key: 'finished', value: false }));
};
export const verifyCart = beforeCheckout => async dispatch => {
	try {
		if (beforeCheckout) {
			dispatch(
				updateCart({
					verifying: true
				})
			);
		}
		await firebaseService.callFunctionByName('organisationCustomerCartVerifyCart', {}, true);
		dispatch(setCartError(null));
		if (beforeCheckout) {
			dispatch(
				updateCart({
					verifying: false,
					verified: true
				})
			);
		}
	} catch (e) {
		if (e && e.details && e.details.detailedCode === 'notEnoughTicketsAvailable') {
			dispatch(
				setCartError({
					type: 'dialog',
					message: `Der er desværre ikke flere pladser på ${e.details.events.join(
						', '
					)}. Billetterne er derfor blevet fjernet fra din kurv.`
				})
			);
		} else if (e && e.details && e.details.detailedCode === 'inactiveOrDeletedProduct') {
			dispatch(
				setCartError({
					type: 'dialog',
					message: `Produkt ${e.details.products.join(
						', '
					)} er desværre udsolgt, og er derfor fjernet fra din kurv.`
				})
			);
		} else if (e && e.details && e.details.detailedCode === 'orderNotFound') {
			dispatch(
				setCartError({
					type: 'dialog',
					message: `Produkt ${e.details.products.join(
						', '
					)} er desværre udsolgt, og er derfor fjernet fra din kurv.`
				})
			);
		} else if (e && e.details && e.details.detailedCode === 'tooManyOrderLines') {
			dispatch(
				setCartError({
					type: 'dialog',
					message: 'Du kan ikke have mere end 90 produkter i kurven på samme tid.'
				})
			);
		} else {
			dispatch(
				setCartError({
					type: 'dialog',
					message: `Der er sket en ukendt fejl, prøv igen.`
				})
			);
		}
		console.error(e);
	}
};

export const updateCartDraft = async (customerUid, orderUid, data) => {
	const db = firebaseService.getOrganisationRootDB();
	await db.collection('customers').doc(customerUid).collection('orderDrafts').doc(orderUid).update(data);
};

export const addProductsToOrder = lines => async dispatch => {
	dispatch(setCartLoading({ key: 'orderLines', value: true }));
	try {
		await firebaseService.callFunctionByName(
			'organisationCustomerCartOrderlineCreateOrderline',
			{
				lines
			},
			true
		);
	} catch (e) {
		dispatch(
			setCartError({
				type: 'dialog',
				message: e.message
			})
		);
	} finally {
		dispatch(setCartLoading({ key: 'orderLines', value: false }));
	}
};
export const submitOrder = () => async dispatch => {
	dispatch(setCartLoading({ key: 'finished', value: true }));
	try {
		const currentLanguageLocalStorage = localStorage.getItem('language')
			? JSON.parse(localStorage.getItem('language'))
			: null;
		await firebaseService.callFunctionByName(
			'organisationCustomerCartSubmitCart',
			{
				language:
					currentLanguageLocalStorage && currentLanguageLocalStorage.isoCode
						? currentLanguageLocalStorage.isoCode
						: null
			},
			true
		);
		dispatch(setCartError(null));
	} catch (e) {
		if (e.details.detailedCode === 'notEnoughTicketsAvailable') {
			dispatch(
				setCartError({
					type: 'dialog',
					message: `Der er desværre ikke flere pladser på ${e.details.events.join(
						', '
					)}. Billetterne er derfor blevet fjernet fra din kurv.`
				})
			);
		} else if (e.details.detailedCode === 'inactiveOrDeletedProduct') {
			dispatch(
				setCartError({
					type: 'dialog',
					message: `Produkt ${e.details.products.join(
						', '
					)} er desværre udsolgt, og er derfor fjernet fra din kurv.`
				})
			);
		} else if (e.details.detailedCode === 'tooManyOrderLines') {
			dispatch(
				setCartError({
					type: 'dialog',
					message: 'Du kan ikke have mere end 90 produkter i kurven på samme tid.'
				})
			);
		} else if (e.details.detailedCode === 'orderNotFound') {
			dispatch(
				setCartError({
					type: 'dialog',
					message: `Produkt ${e.details.products.join(
						', '
					)} er desværre udsolgt, og er derfor fjernet fra din kurv.`
				})
			);
		}
		dispatch(setCartLoading({ key: 'finished', value: false }));
	}
};

export const cloneOrder = async (customerUid, orderUid) => {
	await firebaseService.callFunctionByName('organisationCustomerCartCloneCart', {
		customerUid,
		orderUid
	});
};

export const applyCode = code => async dispatch => {
	dispatch(setCartLoading({ key: 'code', value: true }));
	try {
		await firebaseService.callFunctionByName('organisationCustomerCartApplyCode', { code }, true);
	} catch (e) {
		dispatch(
			setCartError({
				type: 'dialog',
				messageKey: `CART_APPLY_CODE_ERROR`
			})
		);
	} finally {
		dispatch(setCartLoading({ key: 'code', value: false }));
	}
};
export const updateOrderLineAmount = (orderLines, orderLineUid, amount) => async dispatch => {
	const changedIndex = orderLines.findIndex(ol => ol.id === orderLineUid);
	const newOrderLines = _.cloneDeep(orderLines);
	const oldAmount = newOrderLines[changedIndex].qty;
	newOrderLines[changedIndex] = { ...newOrderLines[changedIndex], qty: amount };
	dispatch(setCartOrderLines(newOrderLines));
	dispatch(setCartLoading({ key: 'totals', value: true }));
	dispatch(setCartLoading({ key: orderLineUid, value: true }));
	try {
		await firebaseService.callFunctionByName(
			'organisationCustomerCartOrderlineUpdateOrderline',
			{
				qty: amount,
				orderLineUid
			},
			true
		);
	} catch (e) {
		dispatch(
			setCartError({
				type: 'dialog',
				message:
					'Der er ikke nok billetter tilgængelige i den valgte kombination på denne dato. Prøv en anden dato eller reguler antallet af billetter'
			})
		);
		const oldOrderLines = _.cloneDeep(orderLines);
		oldOrderLines[changedIndex] = { ...oldOrderLines[changedIndex], qty: oldAmount };
		dispatch(setCartOrderLines(oldOrderLines));
	} finally {
		dispatch(setCartLoading({ key: orderLineUid, value: false }));
		dispatch(setCartLoading({ key: 'totals', value: false }));
	}
};

export const deleteOrderLine = (orderLineUid, type) => async dispatch => {
	dispatch(setCartLoading({ key: 'orderLines', value: true }));
	if (type === 'code') {
		dispatch(setCartLoading({ key: 'code', value: true }));
	}
	await firebaseService.callFunctionByName('organisationCustomerCartOrderlineDeleteOrderline', {
		orderLineUid
	});
	dispatch(setCartLoading({ key: 'orderLines', value: false }));
	if (type === 'code') {
		dispatch(setCartLoading({ key: 'code', value: false }));
	}
};
export const removeCode = code => async dispatch => {
	dispatch(setCartLoading({ key: 'code', value: true }));
	await firebaseService.callFunctionByName('organisationCustomerCartRemoveCode', {
		code
	});
	dispatch(setCartLoading({ key: 'false', value: true }));
};

export const selectUser = state => state.shared.user.user;
export const selectTickets = state => state.shared.user.tickets;
export const selectOrders = state => state.shared.user.orders;
export const selectSubscriptions = state => state.shared.user.subscriptions;
export const selectSubscriptionOrders = state => state.shared.user.subscriptionOrders;
export const selectLoading = state => state.shared.user.loading;
export const selectCart = state => state.shared.user.cart;
export const selectCartOrderLines = state => state.shared.user.cartOrderLines;
export const selectCartDraft = state => state.shared.user.cartDraft;
export const selectCartLoading = state => state.shared.user.cartLoading;
export const selectCartLoadingById = (state, id) => state.shared.user.cartLoading[id];
export const selectCartError = state => state.shared.user.cartError;
